'use client'

import { ChevronLeft, ChevronDown } from 'lucide-react'
import { type ReactNode } from 'react'
import FilterSvg from '~/icons/ic_filter'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { BookingStep, useBookingFlow } from '~/stores/booking-flow'

interface BreadcrumbMobileProps {
  title?: string
  dateRange?: string
  guestInfo?: string
  onDateClick?: () => void
  onGuestClick?: () => void
  onFilterClick?: () => void
  showDetailInfo?: boolean
  onBackToSearch?: () => void
  children?: ReactNode
  isBack?: boolean
  className?: string
}

export default function BreadcrumbMobile({
  title: titleProp,
  dateRange,
  guestInfo,
  onDateClick,
  onGuestClick,
  onFilterClick,
  showDetailInfo = true,
  onBackToSearch,
  children,
  isBack = true,
  className = '',
}: BreadcrumbMobileProps) {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const { currentStep } = useBookingFlow()
  return (
    <div className={`sticky inset-x-0 top-0 z-40 bg-[#8B5E3C] md:hidden ${className}`}>
      <div className="flex items-center gap-1 px-4 py-3">
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            {isBack && onBackToSearch ? (
              <button
                type="button"
                className="flex items-center justify-center font-lexend text-[12px] font-medium leading-6 text-white"
                aria-label={t('common.back') || 'Back'}
                onClick={onBackToSearch}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            ) : (
              <div className="w-5" />
            )}

            <h1 className="flex-1 text-center font-lexend text-base font-medium text-white">
              {titleProp ?? t('search.title')}
            </h1>

            {onFilterClick ? (
              <button
                type="button"
                aria-label="Filter"
                data-filter-btn="true"
                onClick={onFilterClick}
                className="flex items-center justify-center font-lexend text-[12px] font-medium leading-6 text-white"
              >
                <FilterSvg className="h-5 w-5" />
              </button>
            ) : (
              <div className="w-5" />
            )}
          </div>

          {showDetailInfo && dateRange && (
            <div className="mt-2 flex items-center justify-around gap-1 text-[12px] text-white">
              <button
                type="button"
                className="flex items-center gap-1"
                onClick={() => {
                  onDateClick?.()
                  if (currentStep === BookingStep.LIST) {
                    onBackToSearch?.()
                  }
                }}
              >
                {dateRange}
              </button>

              {guestInfo && (
                <>
                  <span className="mx-1 text-[#EBE3D6]">|</span>
                  <button
                    type="button"
                    className="flex items-center gap-1"
                    onClick={() => {
                      onGuestClick?.()
                      if (currentStep === BookingStep.LIST) {
                        onBackToSearch?.()
                      }
                    }}
                  >
                    {guestInfo}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {children}
    </div>
  )
}
